import { useState, useEffect, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { captureError } from "@/lib/errorReporting";
import { TopBar } from "@/components/ui";
import Icon from "@/components/ui/Icon";
import Button from "@/components/ui/Button";
import { useRealtimeCabal } from "@/hooks/useRealtimeCabal";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

/* ─── Types ─── */
interface CabalDetail {
    _id: string;
    name: string;
    ticker: string;
    slug: string;
    lore?: string;
    imageUrl?: string;
    status: "recruiting" | "launched" | "failed";
    depositTarget: number;
    depositCurrent: number;
    targetMcap: number;
    leaderWallet: string;
    leaderDeposit: number;
    memberCount: number;
    inviteCode: string;
    minDeposit: number;
    maxDeposit: number;
    recruitingDeadline?: string;
}

/* ─── Countdown Hook ─── */
function useCountdown(deadline: string | undefined) {
    const [timeLeft, setTimeLeft] = useState<{
        hours: number;
        minutes: number;
        seconds: number;
        expired: boolean;
    }>({ hours: 0, minutes: 0, seconds: 0, expired: false });

    useEffect(() => {
        if (!deadline) return;

        function calc() {
            const ms = new Date(deadline!).getTime() - Date.now();
            if (ms <= 0) {
                setTimeLeft({ hours: 0, minutes: 0, seconds: 0, expired: true });
                return;
            }
            const h = Math.floor(ms / 3_600_000);
            const m = Math.floor((ms % 3_600_000) / 60_000);
            const s = Math.floor((ms % 60_000) / 1_000);
            setTimeLeft({ hours: h, minutes: m, seconds: s, expired: false });
        }

        calc();
        const interval = setInterval(calc, 1000);
        return () => clearInterval(interval);
    }, [deadline]);

    return timeLeft;
}

/* ════════════════════════════════════════════
   Join Cabal Page — invite link landing
   ════════════════════════════════════════════ */
export default function JoinCabalPage(): ReactNode {
    const { inviteCode } = useParams<{ inviteCode: string }>();
    const navigate = useNavigate();
    const posthog = usePostHog();
    const [cabal, setCabal] = useState<CabalDetail | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!inviteCode) return;
        const controller = new AbortController();
        setLoading(true);

        fetch(`${API_BASE}/api/cabals/invite/${inviteCode}`, { signal: controller.signal })
            .then((r) => {
                if (!r.ok) throw new Error("Invalid invite");
                return r.json();
            })
            .then((data) => setCabal(data))
            .catch((err) => {
                if (err.name !== "AbortError") captureError("Fetch error:", err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [inviteCode]);

    const countdown = useCountdown(cabal?.recruitingDeadline);

    // ─── Real-time deposit progress from Firebase RTDB ───
    const realtime = useRealtimeCabal(cabal?.slug);
    const liveDepositCurrent = realtime.depositProgress?.current ?? cabal?.depositCurrent ?? 0;
    const liveDepositTarget = realtime.depositProgress?.target ?? cabal?.depositTarget ?? 0;

    const pad = (n: number) => String(n).padStart(3, "0");
    const timeDisplay = !cabal?.recruitingDeadline
        ? "—"
        : countdown.expired
            ? "Resolving..."
            : `${countdown.hours > 0 ? pad(countdown.hours) + "h" : ""} ${countdown.minutes}m ${countdown.seconds}s`;

    const progress = liveDepositTarget > 0
        ? Math.min((liveDepositCurrent / liveDepositTarget) * 100, 100)
        : 0;

    const isFilled = liveDepositCurrent >= liveDepositTarget;
    const canJoin = cabal?.status === "recruiting" && !isFilled && !countdown.expired;

    const titleText = cabal?.status === "launched"
        ? "Coin Launched"
        : cabal?.status === "failed"
            ? "Round Ended"
            : "Codev In Progress";

    return (
        <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden max-w-[430px] mx-auto border-x border-white/5 shadow-2xl bg-[#0a0a0a]">
            <TopBar
                variant="minimal"
                title=""
                showBack
                onBack={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/", { replace: true }))}
            />

            <main className="relative z-10 flex-1 flex flex-col px-5 pt-2 pb-36 overflow-y-auto">
                {loading ? (
                    <div className="space-y-4 animate-pulse">
                        {/* Title skeleton */}
                        <div className="mb-4">
                            <div className="h-[42px] w-[260px] bg-white/5 rounded mb-[6px]" />
                            <div className="flex items-center gap-3 opacity-50">
                                <div className="h-3 w-12 bg-white/5 rounded" />
                                <div className="w-px h-3 bg-white/5" />
                                <div className="h-3 w-20 bg-white/5 rounded" />
                                <div className="w-px h-3 bg-white/5" />
                                <div className="h-3 w-16 bg-white/5 rounded" />
                            </div>
                        </div>
                        {/* Card skeleton */}
                        <div
                            className="rounded-[12px] p-4"
                            style={{
                                background: "linear-gradient(to bottom, rgba(115,115,115,0), rgba(115,115,115,0.2))",
                                border: "0.5px solid rgba(153,153,153,0.5)",
                            }}
                        >
                            {/* Token header */}
                            <div className="flex items-center gap-4 mb-6">
                                <div className="size-[64px] rounded-full bg-white/5 shrink-0" />
                                <div className="flex flex-col gap-[8px]">
                                    <div className="h-6 w-20 bg-white/5 rounded" />
                                    <div className="h-5 w-14 bg-white/5 rounded-[4px]" />
                                </div>
                            </div>
                            <div className="h-px w-full bg-white/10 mb-6" />
                            {/* Stats */}
                            <div className="flex items-start gap-4 mb-5">
                                <div className="flex-1 flex flex-col gap-[6px]">
                                    <div className="h-3.5 w-20 bg-white/5 rounded" />
                                    <div className="h-5 w-28 bg-white/5 rounded" />
                                </div>
                                <div className="flex-1 flex flex-col gap-[6px] items-end">
                                    <div className="h-3.5 w-16 bg-white/5 rounded" />
                                    <div className="h-5 w-24 bg-white/5 rounded" />
                                </div>
                            </div>
                            {/* Progress bar */}
                            <div className="h-[14px] w-full bg-[rgba(255,255,255,0.06)] rounded-full" />
                        </div>
                    </div>
                ) : !cabal ? (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-white/40 font-[family-name:var(--font-display)]">Invalid or expired invite link</p>
                    </div>
                ) : (
                    <>
                        {/* ─── Title + Feature Pills ─── */}
                        <div className="mb-4">
                            <h1 className="text-[clamp(34px,4.5dvh,42px)] font-bold text-white tracking-[-0.84px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {titleText}
                            </h1>
                            <div className="flex items-center gap-3 mt-[6px] opacity-50">
                                <div className="flex items-center gap-1">
                                    {/* <Icon name="token" size={14} /> */}
                                    <span className="text-[12px] font-semibold text-white tracking-[-0.12px] leading-[1.27] font-[family-name:var(--font-display)]">
                                        Join launch pool, be the first to buy and share creator fees
                                    </span>
                                </div>
                                {/* <div className="w-px self-stretch bg-[#d9d9d9]" />
                                <div className="flex items-center gap-1">
                                    <Icon name="external-link" size={14} />
                                    <span className="text-[12px] font-semibold text-white tracking-[-0.12px] leading-[1.27] font-[family-name:var(--font-display)]">
                                        Shared Launch
                                    </span>
                                </div>
                                <div className="w-px self-stretch bg-[#d9d9d9]" />
                                <div className="flex items-center gap-1">
                                    <Icon name="shield-check" size={14} />
                                    <span className="text-[12px] font-semibold text-white tracking-[-0.12px] leading-[1.27] font-[family-name:var(--font-display)]">
                                        No Front-Run
                                    </span>
                                </div> */}
                            </div>
                        </div>

                        {/* ─── Lore ─── */}
                        {cabal.lore && (
                            <div className="mb-4">
                                <div className="bg-[rgba(255,255,255,0.05)] border-[0.5px] border-[rgba(255,255,255,0.15)] rounded-[12px] p-4">
                                    <p className="text-white/60 text-[12px] font-[family-name:var(--font-display)] leading-relaxed">
                                        <span className="text-[#f3ce28] font-semibold">Lore:</span> {cabal.lore}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ─── Launched Hero ─── */}
                        {cabal.status === "launched" && (
                            <div className="mb-4">
                                <div className="bg-[rgba(40,243,104,0.05)] border-[0.5px] border-[rgba(40,243,104,0.22)] rounded-[12px] p-6 text-center relative overflow-hidden">
                                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[rgba(40,243,104,0.1)] flex items-center justify-center">
                                        <Icon name="rocket-ship" size={28} />
                                    </div>
                                    <h2 className="text-[clamp(18px,2.1dvh,20px)] font-bold text-[#4ade80] mb-2 font-[family-name:var(--font-display)]">
                                        CODEV Launched
                                    </h2>
                                    <p className="text-white/50 text-[12px] font-[family-name:var(--font-display)]">
                                        The deposit target of <span className="font-semibold text-white/70">{cabal.depositTarget} SOL</span> was reached.
                                        Tokens have been bought and locked for all members.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ─── Failed Hero ─── */}
                        {cabal.status === "failed" && (
                            <div className="mb-4">
                                <div className="bg-red-500/5 border-[0.5px] border-red-500/20 rounded-[12px] p-6 text-center relative overflow-hidden">
                                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                                        <Icon name="cancel" size={30} />
                                    </div>
                                    <h2 className="text-[clamp(18px,2.1dvh,20px)] font-bold text-red-400 mb-2 font-[family-name:var(--font-display)]">
                                        Round Ended
                                    </h2>
                                    <p className="text-white/50 text-[12px] font-[family-name:var(--font-display)]">
                                        The deposit target of <span className="font-semibold text-white/70">{cabal.depositTarget} SOL</span> was
                                        not met within the staking window. All deposits have been refunded.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ─── Cabal Card ─── */}
                        <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] p-4 mb-4">
                            {/* Token header */}
                            <div className="flex items-center gap-4 mb-6">
                                <div className="size-[64px] rounded-full bg-[#171717] p-[3px] flex items-center justify-center shrink-0">
                                    <div className="relative size-[56px] rounded-full overflow-hidden">
                                        {cabal.imageUrl ? (
                                            <img alt={cabal.ticker} className="w-full h-full object-contain rounded-full" src={cabal.imageUrl} />
                                        ) : (
                                            <div className="w-full h-full rounded-full bg-[#171717] flex items-center justify-center">
                                                <span className="text-2xl font-bold text-white font-[family-name:var(--font-display)]">
                                                    {cabal.ticker.toUpperCase().charAt(0)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="text-[clamp(22px,2.8dvh,26px)] font-medium text-white tracking-[-0.52px] leading-[1.13] font-[family-name:var(--font-display)] truncate max-w-[200px]">
                                    {cabal.ticker.toUpperCase()}
                                </p>
                                <div className="ml-auto bg-[rgba(255,255,255,0.09)] rounded-[4px] px-2.5 py-1 shrink-0">
                                    <p className="text-[12px] font-medium text-white leading-[1.15] font-[family-name:var(--font-display)]">
                                        {cabal.status === "launched" ? "LAUNCHED" : cabal.status === "failed" ? "ENDED" : isFilled ? "FILLED" : "LIVE"}
                                    </p>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="h-px w-full bg-white/10 mb-6" />

                            {/* Stats */}
                            <div className="flex items-start gap-4 mb-5">
                                <div className="flex-1 flex flex-col gap-[6px]">
                                    <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] font-[family-name:var(--font-display)]">
                                        Amount Filled
                                    </p>
                                    <p className="text-[clamp(18px,2.1dvh,20px)] font-medium text-white tracking-[-0.4px] leading-[1.15] font-[family-name:var(--font-display)]">
                                        {parseFloat(liveDepositCurrent.toFixed(4))} / {parseFloat(liveDepositTarget.toFixed(4))} SOL
                                    </p>
                                </div>
                                <div className="flex-1 flex flex-col gap-[6px] items-end">
                                    <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] font-[family-name:var(--font-display)]">
                                        {cabal.status === "launched" ? "Status" : cabal.status === "failed" ? "Status" : "Time Left"}
                                    </p>
                                    <p className={`text-[clamp(18px,2.1dvh,20px)] font-medium tracking-[-0.4px] leading-[1.15] text-right font-[family-name:var(--font-display)] tabular-nums ${cabal.status === "launched"
                                        ? "text-[#4ade80]"
                                        : cabal.status === "failed"
                                            ? "text-red-400"
                                            : isFilled
                                                ? "text-[#f3ce28]"
                                                : countdown.expired
                                                    ? "text-red-400 animate-pulse"
                                                    : "text-white"
                                        }`}>
                                        {cabal.status === "launched"
                                            ? "Launched"
                                            : cabal.status === "failed"
                                                ? "Ended"
                                                : isFilled
                                                    ? "Fully Filled"
                                                    : timeDisplay}
                                    </p>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="h-[14px] w-full bg-[rgba(255,255,255,0.06)] rounded-full flex items-center overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${cabal.status === "failed" ? "bg-red-400" : "bg-[#f3ce28]"
                                        }`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </main>

            {/* ─── Bottom CTA ─── */}
            {canJoin && cabal && (
                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] px-5 py-4 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 z-50">
                    <Button
                        variant="cta"
                        size="lg"
                        textStyle="v2"
                        fullWidth
                        onClick={() => {
                            posthog.capture("join_cabal_clicked", {
                                cabalSlug: cabal.slug,
                                ticker: cabal.ticker,
                                inviteCode,
                            });
                            navigate(`/codevs/${cabal.slug}/deposit`);
                        }}
                        className="h-[64px] text-[clamp(18px,2.1dvh,20px)]"
                    >
                        Codev {cabal.ticker.toUpperCase()}
                    </Button>
                </div>
            )}
        </div>
    );
}
